import React from 'react';
import { SEOHead } from '../shared/components/SEOHead';
import './styles/terms-of-use.css';

export const TermsOfUsePage: React.FC = () => (
    <>
        <SEOHead seo={{
            title: 'Terms of Use | Arte Craft India',
            description: 'Read the terms and conditions governing your use of the Arte Craft India marketplace platform.',
            keywords: 'terms of use, terms and conditions, user agreement, Arte Craft India',
            canonical: `${window.location.origin}/terms-of-use`,
            ogType: 'website',
            noIndex: false,
        }} />

        <div className="tu-hero">
            <h1>Terms of Use</h1>
            <p className="tu-updated">Last updated: May 2025</p>
        </div>

        <div className="tu-container">

            <p className="tu-intro">
                Welcome to Arte Craft India. By accessing or using our website and marketplace
                platform ("Platform"), you agree to be bound by these Terms of Use ("Terms").
                Please read them carefully before using any part of the Platform. If you do not
                agree to these Terms, please do not use the Platform.
            </p>

            <div className="tu-section">
                <h2>1. Acceptance of Terms</h2>
                <p>
                    By creating an account, placing an order, or otherwise accessing the Platform,
                    you confirm that you are at least 18 years of age, legally capable of entering
                    into a binding contract, and agree to comply with these Terms and our Privacy
                    Policy. If you are using the Platform on behalf of a business, you represent
                    that you have the authority to bind that business to these Terms.
                </p>
            </div>

            <div className="tu-section">
                <h2>2. Use of the Platform</h2>
                <p>
                    Arte Craft India grants you a limited, non-exclusive, non-transferable licence
                    to access and use the Platform for lawful personal or business purposes in
                    accordance with these Terms. You must not:
                </p>
                <ul>
                    <li>Use the Platform for any unlawful purpose or in violation of any applicable law or regulation</li>
                    <li>Reproduce, duplicate, copy, sell, or resell any part of the Platform without our written consent</li>
                    <li>Attempt to gain unauthorised access to any part of the Platform or its related systems</li>
                    <li>Transmit any unsolicited commercial communications (spam)</li>
                    <li>Use automated tools (bots, scrapers) to access or collect data from the Platform</li>
                </ul>
            </div>

            <div className="tu-section">
                <h2>3. User Accounts</h2>
                <p>
                    To access certain features, you must register for an account. You are responsible for:
                </p>
                <ul>
                    <li>Providing accurate, current, and complete registration information</li>
                    <li>Maintaining the confidentiality of your account credentials</li>
                    <li>All activity that occurs under your account</li>
                    <li>Notifying us immediately of any unauthorised use of your account</li>
                </ul>
                <p>
                    We reserve the right to suspend or terminate accounts that violate these Terms,
                    provide false information, or engage in fraudulent or abusive behaviour.
                </p>
            </div>

            <div className="tu-section">
                <h2>4. Seller Accounts</h2>
                <p>
                    Sellers on the Platform are subject to an additional approval workflow. By registering
                    as a seller, you agree to:
                </p>
                <ul>
                    <li>Provide accurate business information including GST details where required</li>
                    <li>List only genuine, accurately described products that you have the right to sell</li>
                    <li>Fulfil orders promptly and maintain the product quality described in your listings</li>
                    <li>Comply with all applicable laws, including consumer protection and tax obligations</li>
                    <li>Accept that Arte Craft India may suspend or terminate your seller account for policy violations</li>
                </ul>
                <p>
                    Seller accounts remain in a PENDING status until reviewed and approved by our team.
                    Approval may be declined at our sole discretion.
                </p>
            </div>

            <div className="tu-section">
                <h2>5. Intellectual Property</h2>
                <p>
                    All content on the Platform — including text, graphics, logos, images, and software —
                    is the property of Arte Craft India or its licensors and is protected by applicable
                    intellectual property laws. You may not reproduce, distribute, or create derivative
                    works from this content without our express written permission.
                </p>
                <p>
                    By uploading or submitting content (such as product images or descriptions), you
                    grant Arte Craft India a non-exclusive, royalty-free, worldwide licence to use,
                    display, and reproduce that content in connection with operating the Platform.
                </p>
            </div>

            <div className="tu-section">
                <h2>6. Orders, Payments, and Pricing</h2>
                <p>
                    All prices displayed on the Platform are in Indian Rupees (INR) and are inclusive
                    of applicable taxes unless stated otherwise. We reserve the right to correct pricing
                    errors and to cancel orders placed at an incorrect price.
                </p>
                <ul>
                    <li>Placing an order constitutes an offer to purchase; acceptance occurs when we confirm the order</li>
                    <li>Payment is processed at the time of order placement via our supported payment methods</li>
                    <li>We are not responsible for additional charges levied by your bank or payment provider</li>
                    <li>Custom quote requests are subject to separate agreement before any payment is taken</li>
                </ul>
            </div>

            <div className="tu-section">
                <h2>7. Returns and Refunds</h2>
                <p>
                    Returns and refunds are handled in accordance with our Refund Policy, which forms
                    part of these Terms. In general:
                </p>
                <ul>
                    <li>Defective or incorrectly shipped items may be returned within 7 days of delivery</li>
                    <li>Custom or personalised orders are non-returnable unless defective</li>
                    <li>Refunds are processed to the original payment method within 7–10 business days of approval</li>
                </ul>
                <p>
                    Please contact us at <a href="mailto:info@artecraftindia.com">info@artecraftindia.com</a> to
                    initiate a return or raise a dispute.
                </p>
            </div>

            <div className="tu-section">
                <h2>8. Prohibited Conduct</h2>
                <p>You agree not to engage in any of the following:</p>
                <ul>
                    <li>Posting false, misleading, or defamatory content about Arte Craft India, its sellers, or users</li>
                    <li>Harassing, threatening, or intimidating other users or sellers</li>
                    <li>Circumventing the Platform to conduct transactions outside of it after initial contact through Arte Craft India</li>
                    <li>Uploading malware, viruses, or any harmful code</li>
                    <li>Manipulating reviews, ratings, or any feedback mechanism</li>
                </ul>
            </div>

            <div className="tu-section">
                <h2>9. Disclaimers</h2>
                <p>
                    The Platform is provided on an "as is" and "as available" basis without warranties
                    of any kind, either express or implied. Arte Craft India does not warrant that the
                    Platform will be uninterrupted, error-free, or free of viruses or other harmful components.
                </p>
                <p>
                    Product descriptions, images, and specifications are provided by sellers and we do
                    not guarantee their accuracy. We encourage buyers to review product details carefully
                    before placing an order.
                </p>
            </div>

            <div className="tu-section">
                <h2>10. Limitation of Liability</h2>
                <p>
                    To the maximum extent permitted by applicable law, Arte Craft India and its directors,
                    employees, and agents shall not be liable for any indirect, incidental, special,
                    consequential, or punitive damages arising from your use of the Platform.
                </p>
                <p>
                    Our total liability to you for any claim arising out of these Terms or your use of
                    the Platform shall not exceed the amount you paid to Arte Craft India in the six
                    months preceding the claim.
                </p>
            </div>

            <div className="tu-section">
                <h2>11. Indemnification</h2>
                <p>
                    You agree to indemnify and hold harmless Arte Craft India, its affiliates, officers,
                    directors, employees, and agents from any claims, liabilities, damages, losses, and
                    expenses (including reasonable legal fees) arising out of or relating to your use of
                    the Platform, your violation of these Terms, or your infringement of any third-party
                    rights.
                </p>
            </div>

            <div className="tu-section">
                <h2>12. Governing Law</h2>
                <p>
                    These Terms are governed by and construed in accordance with the laws of India.
                    Any disputes arising from these Terms or your use of the Platform shall be subject
                    to the exclusive jurisdiction of the courts in New Delhi, India.
                </p>
            </div>

            <div className="tu-section">
                <h2>13. Changes to These Terms</h2>
                <p>
                    We may update these Terms from time to time. When we do, we will revise the
                    "Last updated" date at the top of this page and, for material changes, notify
                    registered users by email. Continued use of the Platform after changes are posted
                    constitutes your acceptance of the revised Terms.
                </p>
            </div>

            <div className="tu-section">
                <h2>14. Contact Us</h2>
                <p>
                    If you have any questions or concerns about these Terms, please contact us:
                </p>
                <div className="tu-contact-box">
                    <p><strong>Arte Craft India</strong></p>
                    <p>280/13 Gali No. 29, Molarband Extn, Badarpur, New Delhi – 110044</p>
                    <p>Email: <a href="mailto:info@artecraftindia.com">info@artecraftindia.com</a></p>
                    <p>Phone: <a href="tel:+919818548369">+91 9818548369</a></p>
                </div>
            </div>

        </div>
    </>
);
